import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
  Pagination,
  DropdownButton,
  Dropdown
} from 'react-bootstrap';
import { FiEdit2, FiTrash2, FiUserCheck, FiUserX, FiActivity, FiPlus } from 'react-icons/fi';
import { gatewaysAPI, customersAPI } from '../services/api';
import { Gateway, GatewaysResponse, Customer } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface GatewayFormData {
  gatewayName: string;
  phone: string;
  location: string;
  description: string;
  ipAddress: string;
  macAddress: string;
  firmwareVersion: string;
  customerId: string;
}

const Gateways: React.FC = () => {
  const { user: currentUser } = useAuth();

  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGateway, setEditingGateway] = useState<Gateway | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [customers, setCustomers] = useState<Customer[]>([]);

  const [formData, setFormData] = useState<GatewayFormData>({
    gatewayName: '',
    phone: '',
    location: '',
    description: '',
    ipAddress: '',
    macAddress: '',
    firmwareVersion: '',
    customerId: '' // superadmin only
  });

  const fetchGateways = useCallback(async () => {
    try {
      setLoading(true);
      const response: GatewaysResponse = await gatewaysAPI.getAll(currentPage, 10);
      setGateways(response.gateways);
      setTotalPages(response.pagination.total);
    } catch (err) {
      setError('Failed to fetch gateways');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchGateways();
  }, [fetchGateways]);

  const fetchCustomers = useCallback(async () => {
    if (currentUser?.role === 'superadmin') {
      try {
        const response = await customersAPI.getAll(1, 100);
        setCustomers(response.customers);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      }
    }
  }, [currentUser?.role]);

  const resetForm = () => {
    setFormData({
      gatewayName: '',
      phone: '',
      location: '',
      description: '',
      ipAddress: '',
      macAddress: '',
      firmwareVersion: '',
      customerId: ''
    });
    setEditingGateway(null);
  };

  const handleShowModal = async (gateway?: Gateway) => {
    if (gateway) {
      setEditingGateway(gateway);
      setFormData({
        gatewayName: gateway.gatewayName || '',
        phone: gateway.phone || '',
        location: gateway.location || '',
        description: gateway.description || '',
        ipAddress: gateway.ipAddress || '',
        macAddress: gateway.macAddress || '',
        firmwareVersion: gateway.firmwareVersion || '',
        customerId:
          typeof gateway.customerId === 'string'
            ? gateway.customerId
            : gateway.customerId?.id || ''
      });
    } else {
      resetForm();
    }
    await fetchCustomers();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setError('');
    setSuccess('');
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Validate customer for superadmin
      if (currentUser?.role === 'superadmin' && !formData.customerId) {
        setError('Please select a customer');
        return;
      }

      const baseData = { ...formData, isActive: true };

      if (currentUser?.role !== 'superadmin') {
        // Remove customerId for customer/user roles
        const { customerId, ...dataNoCustomer } = baseData as any;
        if (editingGateway) {
          await gatewaysAPI.update(editingGateway.id, dataNoCustomer);
          setSuccess('Gateway updated successfully');
        } else {
          await gatewaysAPI.create(dataNoCustomer);
          setSuccess('Gateway created successfully');
        }
      } else {
        if (editingGateway) {
          await gatewaysAPI.update(editingGateway.id, baseData);
          setSuccess('Gateway updated successfully');
        } else {
          await gatewaysAPI.create(baseData);
          setSuccess('Gateway created successfully');
        }
      }

      fetchGateways();
      setTimeout(() => handleCloseModal(), 1200);
    } catch (err: any) {
      setError(err?.message || 'Operation failed');
    }
  };

  const handleToggleStatus = async (gatewayId: string) => {
    try {
      await gatewaysAPI.toggleStatus(gatewayId);
      fetchGateways();
    } catch (err) {
      setError('Failed to toggle gateway status');
    }
  };

  const handleStatusChange = async (
    gatewayId: string,
    status: 'online' | 'offline' | 'maintenance'
  ) => {
    try {
      await gatewaysAPI.updateStatus(gatewayId, status);
      fetchGateways();
    } catch (err) {
      setError('Failed to update gateway status');
    }
  };

  const handleDelete = async (gatewayId: string) => {
    if (window.confirm('Are you sure you want to delete this gateway?')) {
      try {
        await gatewaysAPI.delete(gatewayId);
        fetchGateways();
      } catch (err) {
        setError('Failed to delete gateway');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      online: 'success',
      offline: 'secondary',
      maintenance: 'warning'
    } as const;
    return <Badge bg={(variants as any)[status] || 'secondary'}>{status}</Badge>;
  };

  const renderCustomerName = (customer: Gateway['customerId']) => {
    if (!customer) return '-';
    if (typeof customer === 'string') return customer.slice(0, 6) + '…';
    return (customer as Customer).companyName || '-';
  };

  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  if (loading && gateways.length === 0) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem', color: '#667eea' }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading gateways...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <div style={{ width: '95%', maxWidth: '1600px', margin: '0 auto' }}>
      <Row className="mb-4">
        <Col>
          <div className="gradient-card p-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h2 className="mb-2 fw-bold">Gateways Management</h2>
              <p className="mb-0 opacity-90">Monitor and manage IoT gateways</p>
            </div>
            <Button variant="primary" onClick={() => handleShowModal()} className="btn-gradient btn-gradient-primary">
              <FiPlus size={16} className="me-2" />Add New Gateway
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="modern-card">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')} className="modern-card">
          {success}
        </Alert>
      )}

      <Row>
        <Col>
          <Card className="modern-card">
            <Card.Body>
              <div className="table-responsive">
                <Table className="modern-table">
                  <thead>
                    <tr>
                      <th>Gateway Name</th>
                      <th>Phone</th>
                      <th>Customer</th>
                      <th>Location</th>
                      <th>Description</th>
                      <th>IP</th>
                      <th>MAC</th>
                      <th>Firmware</th>
                      <th>Status</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gateways.map((g) => (
                      <tr key={g.id}>
                        <td>{g.gatewayName}</td>
                        <td>{g.phone}</td>
                        <td>{renderCustomerName(g.customerId)}</td>
                        <td>{g.location || '-'}</td>
                        <td>{g.description || '-'}</td>
                        <td>{g.ipAddress || '-'}</td>
                        <td>{g.macAddress || '-'}</td>
                        <td>{g.firmwareVersion || '-'}</td>
                        <td>{getStatusBadge(g.status)}</td>
                        <td>
                          <Badge bg={g.isActive ? 'success' : 'danger'}>
                            {g.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleShowModal(g)}
                              title="Edit gateway"
                              aria-label="Edit gateway"
                            >
                              <FiEdit2 size={14} />
                            </Button>
                            <Button
                              variant={g.isActive ? 'outline-warning' : 'outline-success'}
                              size="sm"
                              onClick={() => handleToggleStatus(g.id)}
                            >
                              {g.isActive ? <><FiUserX size={14} className="me-1" /></> : <><FiUserCheck size={14} className="me-1" />Activate</>}
                            </Button>
                             <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(g.id)}
                              title="Delete gateway"
                              aria-label="Delete gateway"
                            >
                              <FiTrash2 size={14} />
                            </Button>
                            <DropdownButton
                              id={`status-${g.id}`}
                              drop="up"
                              title={<><FiActivity size={14} className="me-1" />Set Status</>}
                              variant="outline-secondary"
                              size="sm"
                            >
                              <Dropdown.Item onClick={() => handleStatusChange(g.id, 'online')}>Online</Dropdown.Item>
                              <Dropdown.Item onClick={() => handleStatusChange(g.id, 'offline')}>Offline</Dropdown.Item>
                              <Dropdown.Item onClick={() => handleStatusChange(g.id, 'maintenance')}>Maintenance</Dropdown.Item>
                            </DropdownButton>
                           
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Page {currentPage} of {totalPages}</small>
                <Pagination className="mb-0">
                  <Pagination.Prev disabled={!canGoPrev} onClick={() => canGoPrev && setCurrentPage((p) => p - 1)} />
                  <Pagination.Next disabled={!canGoNext} onClick={() => canGoNext && setCurrentPage((p) => p + 1)} />
                </Pagination>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton className="gradient-card-header border-0">
          <Modal.Title>{editingGateway ? 'Edit Gateway' : 'Add New Gateway'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="p-4">
            <Row className="g-3">
              <Col md={6}>
                <Form.Group controlId="gatewayName">
                  <Form.Label>Gateway Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="gatewayName"
                    value={formData.gatewayName}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="phone">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="location">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="ipAddress">
                  <Form.Label>IP Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="ipAddress"
                    value={formData.ipAddress}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="macAddress">
                  <Form.Label>MAC Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="macAddress"
                    value={formData.macAddress}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="firmwareVersion">
                  <Form.Label>Firmware Version</Form.Label>
                  <Form.Control
                    type="text"
                    name="firmwareVersion"
                    value={formData.firmwareVersion}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="description">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>

              {currentUser?.role === 'superadmin' && (
                <Col md={12}>
                  <Form.Group controlId="customerId">
                    <Form.Label>Customer</Form.Label>
                    <Form.Select
                      name="customerId"
                      value={formData.customerId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Customer</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.companyName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="btn-gradient btn-gradient-primary">
              {editingGateway ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      </div>
    </Container>
  );
};

export default Gateways;